import React from 'react'
import { useTranslation } from 'react-i18next'
import { ZoningEstimateStats } from '@popety-io/types'
import { Loading } from '../../atoms/Loading'
import { Tooltip } from '../../atoms/Tooltip'
import { ZonePolygonMap } from './ZonePolygonMap'
import {
  EstimatePanelContainer,
  MapSection,
  StatsSection,
  StatsTitle,
  StepCard,
  StepCardHighlighted,
  StepNumber,
  StepContent,
  StepTitle,
  StepDescription,
  RangeContainer,
  RangeLabel,
  DotStripContainer,
  DotStripLegend,
  LegendItem,
  LegendDot,
  LegendLine,
  LoadingContainer,
  EmptyContainer,
} from './EstimatePanel.styles'

interface EstimatePanelProps {
  stats?: ZoningEstimateStats | null
  loading?: boolean
  geoCenter?: { lat: number; lon: number }
  geoPolygon?: { type: string; coordinates: number[][][] | number[][][][] }
  landGeoPolygon?: { type: string; coordinates: number[][][] | number[][][][] }
  metricLabel?: string
  value?: number | string
  unit?: string
  metricKey?: string
}

const getStep2DescKey = (metricKey?: string): string => {
  if (!metricKey) return 'metricSourceModal.estimate.step2Desc.cos'

  if (metricKey.startsWith('cos') || metricKey.startsWith('ios'))
    return 'metricSourceModal.estimate.step2Desc.cos'
  if (metricKey.startsWith('cus') || metricKey.startsWith('ius'))
    return 'metricSourceModal.estimate.step2Desc.cus'
  if (metricKey.startsWith('ibus')) return 'metricSourceModal.estimate.step2Desc.ibus'
  if (metricKey.startsWith('im') || metricKey === 'mass_index')
    return 'metricSourceModal.estimate.step2Desc.im'
  if (metricKey.startsWith('vbr')) return 'metricSourceModal.estimate.step2Desc.vbr'
  if (metricKey.startsWith('floor') || metricKey.includes('floor_nb'))
    return 'metricSourceModal.estimate.step2Desc.floor_nb'
  if (metricKey.startsWith('unit') || metricKey.includes('unit_nb'))
    return 'metricSourceModal.estimate.step2Desc.unit_nb'
  if (metricKey.includes('height')) return 'metricSourceModal.estimate.step2Desc.height'

  return 'metricSourceModal.estimate.step2Desc.cos'
}

export const EstimatePanel: React.FC<EstimatePanelProps> = ({
  stats,
  loading,
  geoCenter,
  geoPolygon,
  landGeoPolygon,
  metricLabel,
  value,
  metricKey,
}) => {
  const { t } = useTranslation('ui')

  const safeBuckets = stats?.buckets || []
  const safeDistribution = stats?.distribution
  const displayValue =
    typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : 0
  const scaleMax = Math.max(safeDistribution?.max || 0, displayValue) * 1.05 || 1
  const toX = (v: number) => (v / scaleMax) * 100
  const maxCount = React.useMemo(
    () => safeBuckets.reduce((m, b) => Math.max(m, b.count), 0),
    [safeBuckets]
  )

  if (loading) {
    return (
      <LoadingContainer>
        <Loading
          size="medium"
          variant="spinner"
          text={t('metricSourceModal.estimate.loading')}
          showText
        />
      </LoadingContainer>
    )
  }

  if (!stats || stats.building_count === 0) {
    return (
      <EmptyContainer>
        <div style={{ fontSize: 48, opacity: 0.4 }}>🏗️</div>
        <div>{t('metricSourceModal.estimate.noBuildings')}</div>
      </EmptyContainer>
    )
  }

  const { distribution, building_count, land_count, percentile_used, sample_size } = stats

  const formatVal = (v: number | string) => `${Number(v).toFixed(2)}`

  return (
    <EstimatePanelContainer>
      {geoPolygon && (
        <MapSection>
          <ZonePolygonMap
            geoPolygon={geoPolygon}
            landGeoPolygon={landGeoPolygon}
            geoCenter={geoCenter}
          />
        </MapSection>
      )}

      <StatsSection>
        <StatsTitle>{t('metricSourceModal.estimate.title')}</StatsTitle>

        {/* Step 1 */}
        <StepCard>
          <StepNumber>1</StepNumber>
          <StepContent>
            <StepTitle>{t('metricSourceModal.estimate.step1Title')}</StepTitle>
            <StepDescription>
              {t('metricSourceModal.estimate.step1Desc', {
                buildingCount: building_count,
                landCount: land_count,
              })}
            </StepDescription>
          </StepContent>
        </StepCard>

        {/* Step 2 */}
        <StepCard>
          <StepNumber>2</StepNumber>
          <StepContent>
            <StepTitle>
              {t('metricSourceModal.estimate.step2Title', {
                metric: metricLabel || metricKey || '',
              })}
            </StepTitle>
            <StepDescription>{t(getStep2DescKey(metricKey))}</StepDescription>
          </StepContent>
        </StepCard>

        {/* Step 3 - Highlighted */}
        <StepCardHighlighted>
          <StepNumber $highlighted>3</StepNumber>
          <StepContent>
            <StepTitle>{t('metricSourceModal.estimate.step3Title')}</StepTitle>
            <StepDescription>
              {t('metricSourceModal.estimate.step3Desc', {
                percentile: percentile_used,
                metric: metricLabel || metricKey || '',
                value: formatVal(displayValue),
              })}
            </StepDescription>
          </StepContent>
        </StepCardHighlighted>

        {/* Dot strip visualization */}
        <RangeContainer>
          <RangeLabel>
            {t('metricSourceModal.estimate.dotStripLabel', {
              metric: metricLabel || metricKey || '',
              count: sample_size,
            })}
          </RangeLabel>
          <DotStripContainer>
            {/* Axis line at vertical center */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: 1,
                background: '#e5e7eb',
              }}
            />

            {/* Circles on axis — size proportional to parcel count */}
            {safeBuckets.map((bucket, i) => {
              const xPct = toX(bucket.value)
              const minSize = 6
              const maxSize = 24
              const size =
                maxCount > 0
                  ? minSize + (bucket.count / maxCount) ** 0.6 * (maxSize - minSize)
                  : minSize
              return (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    left: `${xPct}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <Tooltip
                    placement="top"
                    delay={100}
                    maxWidth={180}
                    content={
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background: '#0294df',
                              opacity: 0.6,
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ fontWeight: 600, fontSize: 13 }}>
                            ≈ {formatVal(bucket.value)}
                          </span>
                        </div>
                        <span style={{ color: '#9ca3af', fontSize: 11 }}>
                          {t('metricSourceModal.estimate.dotTooltip', {
                            count: bucket.count,
                          })}
                        </span>
                      </div>
                    }
                  >
                    <div
                      style={{
                        width: size,
                        height: size,
                        borderRadius: '50%',
                        background: '#0294df',
                        opacity: 0.45,
                        cursor: 'default',
                      }}
                    />
                  </Tooltip>
                </div>
              )
            })}

            {/* P25 dashed line */}
            <div
              style={{
                position: 'absolute',
                left: `${toX(distribution.p25)}%`,
                top: 4,
                bottom: 4,
                width: 0,
                borderLeft: '1.5px dashed #0294df',
                opacity: 0.3,
                transform: 'translateX(-50%)',
              }}
            />

            {/* Median dashed line */}
            <div
              style={{
                position: 'absolute',
                left: `${toX(distribution.median)}%`,
                top: 4,
                bottom: 4,
                width: 0,
                borderLeft: '1.5px dashed #0294df',
                opacity: 0.3,
                transform: 'translateX(-50%)',
              }}
            />

            {/* P75 dashed line */}
            <div
              style={{
                position: 'absolute',
                left: `${toX(distribution.p75)}%`,
                top: 4,
                bottom: 4,
                width: 0,
                borderLeft: '1.5px dashed #0294df',
                opacity: 0.3,
                transform: 'translateX(-50%)',
              }}
            />

            {/* Estimated value marker (= percentile value) */}
            <div
              style={{
                position: 'absolute',
                left: `${toX(displayValue)}%`,
                top: 4,
                bottom: 4,
                width: 2,
                background: '#e6a200',
                transform: 'translateX(-50%)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: `${toX(displayValue)}%`,
                top: 0,
                width: 0,
                height: 0,
                borderLeft: '4px solid transparent',
                borderRight: '4px solid transparent',
                borderTop: '5px solid #e6a200',
                transform: 'translateX(-50%)',
              }}
            />
          </DotStripContainer>

          <DotStripLegend>
            <LegendItem>
              <LegendLine $color="#0294df" $dashed />
              <span>
                P25: {formatVal(distribution.p25)} /{' '}
                {t('metricSourceModal.estimate.legendMedian', {
                  value: formatVal(distribution.median),
                })}{' '}
                / P75: {formatVal(distribution.p75)}
              </span>
            </LegendItem>
            <LegendItem>
              <LegendLine $color="#e6a200" />
              <span style={{ color: '#e6a200', fontWeight: 600 }}>
                {t('metricSourceModal.estimate.legendEstimate', {
                  value: formatVal(displayValue),
                })}
              </span>
            </LegendItem>
            <LegendItem>
              <LegendDot style={{ width: 5, height: 5 }} />
              <LegendDot style={{ width: 10, height: 10 }} />
              <span>{t('metricSourceModal.estimate.legendParcelScale')}</span>
            </LegendItem>
          </DotStripLegend>
        </RangeContainer>
      </StatsSection>
    </EstimatePanelContainer>
  )
}
